import React, { useState, useEffect } from 'react';

export default function SettingsPanel({ domain = 'ops', visible = false, onClose }) {
  const [tab, setTab] = useState('personal');
  const [theme, setTheme] = useState(() => localStorage.getItem('devbox-theme') || 'dark');
  const [lang, setLang] = useState(() => localStorage.getItem('devbox-lang') || 'de');
  const [notifs, setNotifs] = useState(() => localStorage.getItem('devbox-notifs') !== 'off');

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme === 'light' ? 'light' : '');
    localStorage.setItem('devbox-theme', theme);
  }, [theme]);

  useEffect(() => { localStorage.setItem('devbox-lang', lang); }, [lang]);
  useEffect(() => { localStorage.setItem('devbox-notifs', notifs ? 'on' : 'off'); }, [notifs]);

  if (!visible) return null;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div onClick={e => e.stopPropagation()} className="eco-modal">
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'var(--gap-md)', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--font-size-lg)' }}>Einstellungen</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 18 }}>×</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
          {[['personal', 'Persoenlich'], ['notifs', 'Notifications'], ['admin', 'Admin']].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex: 1, padding: '8px', fontSize: 'var(--font-size-sm)', background: 'none', border: 'none',
              color: tab === id ? `var(--accent-${domain})` : 'var(--text-muted)', cursor: 'pointer',
              borderBottom: tab === id ? `2px solid var(--accent-${domain})` : '2px solid transparent',
            }}>{label}</button>
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--gap-md)', overflow: 'auto', flex: 1 }}>
          {tab === 'personal' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)' }}>
              <SettingRow label="Theme">
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className={`btn${theme === 'dark' ? ' btn-primary' : ''}`} onClick={() => setTheme('dark')}>Tokyo Night</button>
                  <button className={`btn${theme === 'light' ? ' btn-primary' : ''}`} onClick={() => setTheme('light')}>Light Mode</button>
                </div>
              </SettingRow>
              <SettingRow label="Sprache">
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className={`btn${lang === 'de' ? ' btn-primary' : ''}`} onClick={() => setLang('de')}>Deutsch</button>
                  <button className={`btn${lang === 'en' ? ' btn-primary' : ''}`} onClick={() => setLang('en')}>English</button>
                </div>
              </SettingRow>
              <SettingRow label="Notifications">
                <button className={`btn${notifs ? ' btn-primary' : ''}`} onClick={() => setNotifs(!notifs)}>{notifs ? 'Aktiviert' : 'Deaktiviert'}</button>
              </SettingRow>
            </div>
          )}

          {tab === 'notifs' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Ntfy Topics</div>
              {['devbox-briefing', 'devbox-alerts', 'devbox-backup'].map(topic => (
                <div key={topic} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-sm)' }}>{topic}</span>
                  <span className="badge badge-ok">Abonniert</span>
                </div>
              ))}
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', marginTop: 'var(--gap-sm)' }}>
                Ntfy-App konfigurieren: ntfy.devboxnsa.org, Topics manuell abonnieren.
              </div>
            </div>
          )}

          {tab === 'admin' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Quick Links</div>
              <div style={{ display: 'flex', gap: 'var(--gap-sm)', flexWrap: 'wrap' }}>
                <a className="btn" href="https://auth.devboxnsa.org/if/admin/" target="_blank" rel="noreferrer">Authentik Admin</a>
                <a className="btn" href="https://portainer.devboxnsa.org" target="_blank" rel="noreferrer">Portainer</a>
                <a className="btn" href="https://dockge.devboxnsa.org" target="_blank" rel="noreferrer">Dockge</a>
                <a className="btn" href="https://uptime.devboxnsa.org" target="_blank" rel="noreferrer">Uptime Kuma</a>
              </div>

              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 'var(--gap-sm)' }}>Diese Domain: {domain}.devboxnsa.org</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Row label="Container" value={`${domain}-frontend`} mono />
                <Row label="Port" value={domain === 'ops' ? '8030' : domain === 'sec' ? '8031' : domain === 'crm' ? '8032' : '8033'} mono />
                <Row label="Backend" value="8024 (data-backend)" mono />
                <Row label="Repo" value="devbox/devbox-ecosystem" mono />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SettingRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{label}</span>
      {children}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 'var(--font-size-sm)' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>{value}</span>
    </div>
  );
}
